/* Pictures in the lesson summary.
   Stored in the private Supabase Storage bucket 'lesson-media' under <org>/<student>/<lesson>/<id>.webp,
   readable only by whoever may touch that student (see supabase/21-lesson-media.sql).
   Images are shrunk in the browser first (max 1600 px, WebP, JPEG where WebP can't be encoded),
   so a screenshot ends up at roughly 100–300 KB. */
import { CLOUD, loggedIn, sbRefresh, sbUrl } from './api.js';
import { S } from '../state.js';

const BUCKET='lesson-media';
export const PIC_MAX=12;
const MAX_SIDE=1600;
const urls=new Map();   // path → object URL (this session only)

export const mediaOn=()=>loggedIn();
const orgFor=ses=>{const s=(S.students||[]).find(x=>x.id===ses.studentId);return ses._org||(s&&s._org)||CLOUD.org};
const enc=p=>p.split('/').map(encodeURIComponent).join('/');

async function call(path,opts={},retry=true){
  if(Date.now()>CLOUD.auth.exp)await sbRefresh();
  const r=await fetch(sbUrl('/storage/v1/'+path),{...opts,headers:{apikey:CLOUD.cfg.key,Authorization:'Bearer '+CLOUD.auth.at,...(opts.headers||{})}});
  if([400,401,403].includes(r.status)&&retry&&!opts.body){await sbRefresh();return call(path,opts,false)}
  if(!r.ok){let m='';try{const j=await r.json();m=j.message||j.error||''}catch(e){}throw new Error('HTTP '+r.status+(m?' '+m:''))}
  return r;
}

/* shrink + re-encode; also strips EXIF (location etc.) since only pixels are drawn */
export async function shrink(file){
  let src;
  try{src=await createImageBitmap(file,{imageOrientation:'from-image'})}
  catch(e){src=await new Promise((res,rej)=>{const u=URL.createObjectURL(file);const im=new Image();im.onload=()=>{URL.revokeObjectURL(u);res(im)};im.onerror=()=>{URL.revokeObjectURL(u);rej(new Error('image'))};im.src=u})}
  const w0=src.width||src.naturalWidth,h0=src.height||src.naturalHeight;
  if(!w0||!h0)throw new Error('image');
  const k=Math.min(1,MAX_SIDE/Math.max(w0,h0));
  const w=Math.max(1,Math.round(w0*k)),h=Math.max(1,Math.round(h0*k));
  const c=document.createElement('canvas');c.width=w;c.height=h;
  const g=c.getContext('2d');g.fillStyle='#fff';g.fillRect(0,0,w,h);g.drawImage(src,0,0,w,h);
  if(src.close)src.close();
  const toBlob=(type,q)=>new Promise(res=>c.toBlob(res,type,q));
  let blob=await toBlob('image/webp',.82);
  if(!blob||blob.type!=='image/webp')blob=await toBlob('image/jpeg',.85);   // Safari can't encode WebP
  if(!blob)throw new Error('image');
  return {blob,w,h};
}

export async function uploadPic(ses,file){
  const {blob,w,h}=await shrink(file);
  const id=Math.random().toString(36).slice(2,10)+Date.now().toString(36);
  const ext=blob.type==='image/webp'?'webp':'jpg';
  const path=[orgFor(ses),ses.studentId,ses.id,id+'.'+ext].join('/');
  await call('object/'+BUCKET+'/'+enc(path),{method:'POST',headers:{'Content-Type':blob.type,'x-upsert':'false','cache-control':'31536000'},body:blob});
  urls.set(path,URL.createObjectURL(blob));
  return {id,path,w,h,kb:Math.round(blob.size/1024),at:new Date().toISOString()};
}

export async function picURL(path){
  if(urls.has(path))return urls.get(path);
  const r=await call('object/authenticated/'+BUCKET+'/'+enc(path));
  const u=URL.createObjectURL(await r.blob());urls.set(path,u);return u;
}

export async function deletePics(paths){
  paths=(paths||[]).filter(Boolean);if(!paths.length||!mediaOn())return;
  for(const p of paths){const u=urls.get(p);if(u){URL.revokeObjectURL(u);urls.delete(p)}}
  await call('object/'+BUCKET,{method:'DELETE',headers:{'Content-Type':'application/json'},body:JSON.stringify({prefixes:paths})});
}
/* when lessons go away: remove their pictures too (best effort) */
export function dropLessonPics(list){
  const paths=[];for(const e of list||[])for(const p of (e&&e.pics)||[])paths.push(p.path);
  if(paths.length)deletePics(paths).catch(e=>console.warn('pics',e));
}
